import { ChangeAttrName } from '@common/flare/change-attr-name.enum';
import { attrsToDictionary, encodeAttr } from '@common/flare/util/prosemirror-flare-xml';
import { getAttrs, setAttr } from '@common/html/util/dom';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { getTrackedChangeIdsNodeAttr } from '@common/prosemirror/tracked-changes/tracked-changes';
import { toBoolean, toBooleanString } from '@common/util/bool';
import { forEach, union } from 'lodash';
import { Attrs, DOMOutputSpec, Mark, Node, NodeType, Schema } from 'prosemirror-model';

export const defaultParseRulePriority = 50;

// This is just a more specific type for the array in a DOMOutputSpec
export type DOMOutputSpecArray = Array<string | 0 | DOMOutputSpec | { [attr: string]: string }>;

export interface DOMOutputSpecModifications {
  attrs?: {
    add?: { [name: string]: string };
    set?: { [name: string]: string };
  };
  content?: string;
  tag?: string;
}

// Returns true if value is a DOMOutputSpec dom element
export function isDomElement(value: any): value is Element {
  return !!value.nodeType; // This is the same test that prosemirror uses internally
}

// Returns true if value is a DOMOutputSpec with dom and contentDOM
export function isContentDomOutputSpec(value: any): value is { dom: Element, contentDOM: HTMLElement } {
  return value && typeof value === 'object' && value.hasOwnProperty('dom') && value.hasOwnProperty('contentDOM');
}

// Returns true if value is a DOMOutputSpec
export function isDomOutputSpec(value: any): value is DOMOutputSpec {
  return typeof value === 'string' || Array.isArray(value) || isDomElement(value) || isContentDomOutputSpec(value);
}

// Returns true if value is a DOMOutputSpec attrs object
export function isDomOutputAttrs(value: any): boolean {
  return value && typeof value === 'object' && !value.nodeType && !Array.isArray(value);
}

export function joinAttrValues(valueA: string, valueB: string): string {
  const valuesA = (valueA || '').split(/\s+/);
  const valuesB = (valueB || '').split(/\s+/);

  return union(valuesA, valuesB).join(' ').trim();
}

// Modifies a DOMOutputSpec array with the given mods
export function modifyDomOutputArray(arr: DOMOutputSpecArray, mods: DOMOutputSpecModifications): DOMOutputSpecArray {
  // If the tag should be modified
  if (mods.tag) {
    arr[0] = mods.tag;
  }

  // If the attrs should be modified
  if (mods.attrs) {
    let attrs = arr[1];

    // If the second element is not an object
    if (!isDomOutputAttrs(attrs)) {
      // Then splice in an object to be the attrs
      attrs = {};
      arr.splice(1, 0, attrs);
    }

    // If the attrs should be set on the array
    if (mods.attrs.set) {
      arr[1] = mods.attrs.set;
    }

    // If there are attrs to add to the array
    if (mods.attrs.add) {
      // Copy the attrs over
      forEach(mods.attrs.add, (value, name) => {
        // Join class values
        if (name === 'class') {
          attrs[name] = joinAttrValues(attrs[name], value);
        } else {
          attrs[name] = value;
        }
      });
    }
  }

  // If the content should be modified
  if (mods.content) {
    // Find the content index. It should be 0 (content hole) or a DOMOutputSpec
    let contentIndex = arr.findIndex((value, index) => {
      return index > 0 && (value === 0 || isDomOutputSpec(value));
    });
    if (contentIndex === -1) {
      contentIndex = arr.length;
    }

    arr[contentIndex] = mods.content;
  }

  return arr;
}

// Modifies a DOMOutputSpec dom element with the given mods
export function modifyDomOutputElement(element: Element, mods: DOMOutputSpecModifications): Element {
  // If the attrs should be modified
  if (mods.attrs) {
    // If the attrs should be set on the element
    if (mods.attrs.set) {
      // Remove all the existing attributes on the element
      for (let i = 0, length = element.attributes.length; i < length; i += 1) {
        const attr = element.attributes[i];
        element.removeAttribute(attr.nodeName);
      }

      // Set the new attrs on the element
      forEach(mods.attrs.set, (value, name) => {
        element.setAttribute(name, value);
      });
    }

    // If there are attrs to add to the element
    if (mods.attrs.add) {
      // Set the attrs on the element
      forEach(mods.attrs.add, (value, name) => {
        if (name === 'class') {
          // Update the classList
          if (typeof value === 'string') {
            value.split(' ').forEach(val => {
              val = val.trim();
              if (val) {
                element.classList.add(val);
              }
            });
          }
        } else {
          element.setAttribute(name, value);
        }
      });
    }
  }

  // If the content should be modified
  if (mods.content) {
    element.innerHTML = mods.content;
  }

  return element;
}

// Modifies a DOMOutputSpec with the given mods
export function modifyDomOutputSpec(spec: DOMOutputSpec, mods: DOMOutputSpecModifications) {
  if (Array.isArray(spec)) {
    return modifyDomOutputArray(spec as DOMOutputSpecArray, mods);
  } else if (isDomElement(spec)) {
    return modifyDomOutputElement(spec as Element, mods);
  } else if (isContentDomOutputSpec(spec)) {
    spec.dom = modifyDomOutputElement(spec.dom, mods);
  }

  return spec;
}

/**
 * Converts a string, number, or boolean into a boolean for node attributes when parsing the DOM.
 * String values of true, yes, 1, and 1.0 return true. The check is case-insensitive.
 * String values of false, no, 0, and 0.0 return false. The check is case-insensitive.
 * Any other string value returns null.
 * A number value of zero returns false.
 * Any other number value returns true.
 * A boolean value is simply returned.
 * A nullish value returns null.
 * @param value The string/number/boolean value to convert.
 * @returns Returns true or false depending on thee input.
 */
export function toAttributeBoolean(value: string | number | boolean): boolean {
  return toBoolean(value, null);
}

/**
 * Converts a string, number, or boolean into a boolean string for node attributes when converting to the DOM.
 * If value is a boolean then the string returned is all lowercase and either 'true' or 'false'.
 * If value is not a boolean then undefined is returned.
 * @param value The string/number/boolean value to convert.
 * @param startCase Whether the boolean string should use starting case or not (True/False). Defaults to false.
 * @returns A string version of boolean value.
 */
export function toAttributeBooleanString(value: string | number | boolean): string {
  value = toBoolean(value, null);
  return typeof value === 'boolean' ? toBooleanString(value, false) : undefined;
}

/**
 * Returns an element's attribute values for a set of names.
 * Performs a case-insensitive check for the attribute names.
 * @param element The element to get the attributes from.
 * @param attrs The list of attribute names to get.
 * @returns A dictionary of attribute name/value pairs.
 */
export function getSchemaAttrs(element: Element, attrs: string[]): Dictionary<string> {
  return getAttrs(element, attrs);
}

/**
 * Returns an element's attribute value.
 * Performs a case-insensitive check for the attribute name.
 * @param element The element to get the attribute from.
 * @param attr The attribute name to get.
 * @returns The attribute's value.
 */
export function getSchemaAttr(element: Element, attr: string): string {
  return getAttrs(element, [attr])[attr];
}

/**
 * Returns true if an element has an attribute.
 * Performs a case-insensitive check for the attribute name.
 * @param element The element to check for the attribute.
 * @param attr The attribute name to check.
 * @returns `true` if the element has the attribute, `false` otherwise.
 */
export function hasSchemaAttr(element: Element, attr: string): boolean {
  return element.hasAttribute(attr) || element.hasAttribute(attr.toLowerCase());
}

/**
 * Returns the tracked change attributes for a DOM element.
 * This returns the same attributes that nodeAttrsToDOMAttrs and nodeToDOM use but only includes the tracked change attributes.
 * @param node The ProseMirror node to get the change attributes from.
 * @returns A dictionary of tracked change attribute name/value pairs.
 */
export function nodeTrackedChangeAttrsToDOMAttrs(node: Node): Dictionary<string> {
  const trackedChangeAttrs: Dictionary<string> = {};

  if (node.attrs?.changeList) {
    trackedChangeAttrs.changeList = node.attrs.changeList;
  }

  if (getTrackedChangeIdsNodeAttr(node.attrs)) {
    trackedChangeAttrs[ChangeAttrName.ChangeIds] = getTrackedChangeIdsNodeAttr(node.attrs);
  }

  return nodeAttrsToDOMAttrs(node, trackedChangeAttrs);
}

/**
 * Converts ProseMirror node attrs to DOM attributes that are ready to be set on a DOM element.
 * @param node The ProseMirror node to convert.
 * @param nodeAttrs An optional set of node attributes to convert. If not provided, the node's attributes will be used.
 * @returns A dictionary of DOM attribute name/value pairs.
 */
export function nodeAttrsToDOMAttrs(node: Node | Mark, nodeAttrs?: Attrs): Dictionary<string> {
  const attrs = nodeAttrs ?? node.attrs;
  const attrsSpecs = node.type.spec.attrs;
  const schema = node.type.schema as CollabSchema;
  const domAttrs: Dictionary<string> = {};

  for (let [attributeName, attributeValue] of Object.entries(attrs)) {
    if (attrsSpecs?.[attributeName]?.skipEditorExport) {
      continue;
    }

    // Allow attrsSpecs to define how to convert to editor attrs
    let editorAttrs = attrsSpecs?.[attributeName]?.toEditorAttr?.(attributeValue, node);
    if (!editorAttrs) {
      editorAttrs = {
        [attributeName]: attributeValue
      };
    }

    // Set the editor attrs on the domAttrs
    for (let [editorAttrName, editorAttrValue] of Object.entries(editorAttrs)) {
      if (!schema.skipAttrEditorExport(editorAttrName, editorAttrValue) && typeof editorAttrValue !== 'undefined' && editorAttrValue !== null) {
        if (editorAttrValue === '' && editorAttrName === 'class') {
          continue;
        }

        if (attrsSpecs?.[editorAttrName]?.exportedName) {
          editorAttrName = attrsSpecs[editorAttrName].exportedName;
        }

        domAttrs[editorAttrName] = editorAttrValue.toString();
      }
    }
  }

  return domAttrs;
}

/**
 * Returns the DOMOuputSpec for a ProseMirror node.
 * @param node The ProseMirror node to convert.
 * @param namespaceURI The optional namespace URI to create the element in.
 * @returns The DOMOutputSpec for the node.
 */
export function nodeToDOM(node: Node | Mark, options?: { document?: Document, isVoid?: boolean, namespaceURI?: string, tagName?: string, tagNameBoxLabel?: string, tagNameBoxTooltip?: string, tagNameBoxTooltipContent?: string }): { dom: HTMLElement, contentDOM: HTMLElement } {
  const isTagNameBox = node.type.spec.isTagNameBox ?? false;
  let { document, isVoid, namespaceURI, tagName: editorTagName } = options ?? {};
  document = document ?? (typeof window !== 'undefined' ? window.document : null);
  editorTagName = editorTagName ?? getEditorTagName(node);
  isVoid = isVoid ?? (node.type.spec.isVoid || isTagNameBox) ?? false;

  let dom: HTMLElement;
  if (namespaceURI) {
    dom = document.createElementNS(namespaceURI, editorTagName) as HTMLElement;
  } else {
    dom = document.createElement(editorTagName);
  }

  // Set attributes
  const domAttrs = nodeAttrsToDOMAttrs(node);
  for (let [attributeName, value] of Object.entries(domAttrs)) {
    setAttr(dom, attributeName, value);
  }

  if (isTagNameBox) {
    applyTagNameBoxTooltip(dom, node, document!, options);
  }

  return { dom, contentDOM: isVoid ? undefined : dom };
}

/**
 * Applies the tag name box label and tooltip popover to a DOM element.
 * Builds the label showing the tag name, builds the tooltip showing the XML source with
 * syntax highlighting (or custom text), and sets up click-to-toggle with light-dismiss
 * and Escape key support.
 * @param dom The tag name box DOM element to apply the tooltip to.
 * @param node The ProseMirror node for the tag name box.
 * @param doc The document to create elements in.
 * @param options Optional overrides for the label text, tooltip text, or tooltip content.
 */
function applyTagNameBoxTooltip(dom: HTMLElement, node: Node | Mark, doc: Document, options?: { tagNameBoxLabel?: string, tagNameBoxTooltip?: string, tagNameBoxTooltipContent?: string }) {
  // Use getSourceTagName so nodes that derive their tag name from attrs (e.g., unknown) render the dynamic name in the label and tooltip.
  const specTagName = getSourceTagName(node);
  const tooltip = options?.tagNameBoxTooltip
    ? buildTagNameBoxTooltipFromText(options.tagNameBoxTooltip, doc)
    : buildTagNameBoxTooltip(node, specTagName, doc, options?.tagNameBoxTooltipContent);
  const label = doc.createElement('mc-lwe-tag-name-box-label');
  label.textContent = options?.tagNameBoxLabel ?? `<${specTagName}>`;
  dom.classList.add('mc-lwe-code-popover-scope', 'mc-lwe-code-popover-anchor');
  dom.appendChild(label);
  tooltip.classList.add('mc-lwe-code-popover');
  tooltip.setAttribute('popover', 'auto');
  dom.appendChild(tooltip);

  // Toggle the popover on click with light-dismiss support.
  // With popover="auto", clicking outside the popover triggers light-dismiss which closes it
  // before the click handler runs. To allow the tag name box click to also close the popover,
  // we track whether the popover was open at mousedown (before light-dismiss) and skip
  // reopening it if so.
  // popoverTargetElement only works on <button>/<input> so we use a click listener instead.
  if (dom.addEventListener) {
    let wasOpen = false;
    dom.addEventListener('mousedown', () => {
      wasOpen = tooltip.matches(':popover-open') ?? false;
    });
    dom.addEventListener('click', () => {
      // If the tag name box is being viewed by an SME then disable the tooltip.
      // This is done by checking for a class name because there is no good communication line from the Angular component to the ProseMirror toDOM function.
      // But checking for a class name in the DOM is a very simple and effective solution.
      if (!wasOpen && !dom.closest('.mc-flare-text-editor-sme')) {
        (tooltip as any).showPopover();
        dom.ownerDocument.addEventListener('keydown', onEscape, true);
      }
    });
    const onEscape = (e: KeyboardEvent) => {
      // Clean up if the element was disconnected (e.g., ProseMirror re-rendered the node)
      if (!dom.isConnected) {
        dom.ownerDocument.removeEventListener('keydown', onEscape, true);
        return;
      }
      if (e.key === 'Escape') {
        (tooltip as any).hidePopover();
        dom.ownerDocument.removeEventListener('keydown', onEscape, true);
      }
    };
    tooltip.addEventListener('toggle', (e: Event) => {
      if ((e as any).newState === 'closed') {
        dom.ownerDocument.removeEventListener('keydown', onEscape, true);
      }
    });
  }
}

/**
 * Appends a custom element with the given text to the parent element.
 * Used to build syntax-highlighted XML in tag name box tooltips.
 * @param parent The parent element to append to.
 * @param text The text content for the element.
 * @param elementTagName The tag name for the element.
 * @param doc The document to create the element in.
 */
function appendXmlElement(parent: HTMLElement, text: string, elementTagName: string, doc: Document, className?: string) {
  const element = doc.createElement(elementTagName);
  element.textContent = text;
  if (className) {
    element.classList.add(className);
  }
  parent.appendChild(element);
}

/**
 * Builds a tooltip DOM element for a tag name box node that displays the node's XML source
 * with syntax highlighting.
 * @param node The ProseMirror node to build the tooltip for.
 * @param tagName The XML tag name for the node.
 * @param doc The document to create elements in.
 * @returns The tooltip element.
 */
function buildTagNameBoxTooltip(node: Node | Mark, tagName: string, doc: Document, textContent?: string): HTMLElement {
  const attrs = attrsToDictionary(node.attrs, node, {});
  const tooltip = doc.createElement('mc-lwe-tag-name-box-tooltip');

  // Build syntax-highlighted XML: <tagName attr1="value1" attr2="value2" />
  appendXmlElement(tooltip, '<', 'mc-lwe-tag-name-box-tooltip-xml-bracket', doc, 'mc-lwe-code-popover-xml-bracket');
  appendXmlElement(tooltip, tagName, 'mc-lwe-tag-name-box-tooltip-xml-tag-name', doc, 'mc-lwe-code-popover-xml-tag-name');

  for (const [attrName, attrValue] of Object.entries(attrs)) {
    appendXmlElement(tooltip, ` ${attrName}`, 'mc-lwe-tag-name-box-tooltip-xml-attr-name', doc, 'mc-lwe-code-popover-xml-attr-name');
    appendXmlElement(tooltip, '=', 'mc-lwe-tag-name-box-tooltip-xml-punct', doc, 'mc-lwe-code-popover-xml-punct');
    appendXmlElement(tooltip, `"${encodeAttr(attrValue)}"`, 'mc-lwe-tag-name-box-tooltip-xml-attr-value', doc, 'mc-lwe-code-popover-xml-attr-value');
  }

  // Use isVoid from the node spec
  const isVoidNode = node.type.spec.isVoid ?? false;
  if (isVoidNode) {
    appendXmlElement(tooltip, ' />', 'mc-lwe-tag-name-box-tooltip-xml-bracket', doc, 'mc-lwe-code-popover-xml-bracket');
  } else {
    appendXmlElement(tooltip, '>', 'mc-lwe-tag-name-box-tooltip-xml-bracket', doc, 'mc-lwe-code-popover-xml-bracket');
    appendXmlElement(tooltip, textContent ?? ' ... ', 'mc-lwe-tag-name-box-tooltip-xml-content', doc, 'mc-lwe-code-popover-xml-content');
    appendXmlElement(tooltip, '</', 'mc-lwe-tag-name-box-tooltip-xml-bracket', doc, 'mc-lwe-code-popover-xml-bracket');
    appendXmlElement(tooltip, tagName, 'mc-lwe-tag-name-box-tooltip-xml-tag-name', doc, 'mc-lwe-code-popover-xml-tag-name');
    appendXmlElement(tooltip, '>', 'mc-lwe-tag-name-box-tooltip-xml-bracket', doc, 'mc-lwe-code-popover-xml-bracket');
  }

  return tooltip;
}

/**
 * Builds a tooltip DOM element with plain text content for tag name box nodes that have
 * custom source representations (e.g., comments, CDATA sections).
 * @param text The text content to display in the tooltip.
 * @param doc The document to create elements in.
 * @returns The tooltip element.
 */
function buildTagNameBoxTooltipFromText(text: string, doc: Document): HTMLElement {
  const tooltip = doc.createElement('mc-lwe-tag-name-box-tooltip');
  tooltip.textContent = text;
  return tooltip;
}

/**
 * Returns a DOMOutputSpec for a ProseMirror node that renders the node as readonly in the editor.
 * This is used for form elements that should not be editable in the editor but should still be rendered as form elements in the DOM.
 * The DOMOutputSpec returned by this function is the same as nodeToDOM except it also sets the readonly attribute to true and tabindex to -1 on the DOM element.
 * @param node The ProseMirror node to convert.
 * @returns The DOMOutputSpec for the node with readonly modifications.
 */
export function readonlyToDOM(node: Node): DOMOutputSpec {
  const outputSpec = nodeToDOM(node);
  outputSpec.dom.setAttribute('readonly', 'true');
  outputSpec.dom.setAttribute('tabindex', '-1');
  return outputSpec;
}

/**
 * Returns the tag name used to render the node in the editor.
 * @param node The ProseMirror node or mark to get the tag name for.
 * @returns The tag name used in the editor.
 */
export function getEditorTagName(node: Node | Mark): string {
  return node.type.spec.editorTagName ?? node.type.spec.tagName ?? node.type.name;
}

/**
 * Returns the tag name used in the source.
 * @param node The ProseMirror node or mark to get the source tag name for.
 * @returns The tag name used in the source.
 */
export function getSourceTagName(node: Node | Mark): string {
  return node.type.spec.toSourceTagName?.(node as Node & Mark) ?? node.type.spec.tagName ?? node.type.name;
}

/** Returns the NodeType in the schema that corresponds to the given tag name.
 * @param schema The ProseMirror schema to search for the NodeType.
 * @param tagName The tag name to find the NodeType for.
 * @returns The NodeType that corresponds to the given tag name, or undefined if no matching NodeType is found.
*/
export function getNodeTypeByTagName(schema: Schema, tagName: string): NodeType | undefined {
  return Object.values(schema.nodes).find(nodeType => {
    const nodeTagName = nodeType.spec.tagName ?? nodeType.name;;
    return nodeTagName === tagName;
  });
}
